/**
 * 数字资产 V1 · 前端类型(与 apps/api/app/schemas/brain.py 对齐)。
 */

export type BrainKind = 'character' | 'location' | 'prop' | 'voice' | 'bgm' | 'camera_move';
export type BrainAssetStatus = 'drafting' | 'locked';
export type BrainTaskStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'partial'
  | 'failed';

export interface BrainCandidate {
  id: string;
  url?: string;
  error?: string;
  picked?: boolean;
}

export interface BrainGeneration {
  runId: string;
  prompt: string;
  count: number;
  createdAt: string;
  candidates: BrainCandidate[];
}

export interface BrainAsset {
  assetId: string;
  tenantId?: string | null;
  kind: BrainKind;
  name: string;
  attributes: Record<string, unknown>;
  referenceImages: string[];
  generations: BrainGeneration[];
  lockedImageUrl?: string | null;
  lockedRunId?: string | null;
  lockedCandidateId?: string | null;
  status: BrainAssetStatus;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  sortOrder?: number;
}

export interface BrainAssetListResponse {
  items: BrainAsset[];
  total: number;
}

export interface BrainGenTask {
  taskId: string;
  assetId: string;
  kind: BrainKind;
  prompt: string;
  count: number;
  size?: string;
  style?: string | null;
  referenceImages: string[];
  status: BrainTaskStatus;
  candidates: BrainCandidate[];
  error?: string | null;
  progress: number;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface BrainReferenceUploadResponse {
  url: string;
  name: string;
}

export interface BrainTemplateItem {
  url: string;
  assetId?: string | null;
  assetName?: string | null;
}

export interface BrainTemplateListResponse {
  items: BrainTemplateItem[];
}

// ---------- 运镜库 ----------

export interface CameraMoveStep {
  stepNo: number;
  shotType: string;
  cameraMovement: string;
  notes: string;
}

export interface CameraMoveAttributes {
  description?: string;
  movementSequence: CameraMoveStep[];
  suitableScenes: string[];
  isPreset?: boolean;
}

// ---------- 前端展示用维度 schema(用于 wizard 表单收集) ----------

export interface CharacterAttributes {
  gender?: string;
  age?: string;
  role?: string;
  personality?: string[];
  preferences?: string;
  filmStyle?: string;
  appearance?: string;
  extra?: string;
  // 新增深层人设字段
  desire?: string;
  fear?: string;
  flaw?: string;
  contradiction?: string;
  signatureBehavior?: string;
  subtext?: string;
  speakingStyle?: string;
  relations?: string;
}

export interface LocationAttributes {
  kind?: string;
  atmosphere?: string;
  time?: string;
  lighting?: string;
  visualStyle?: string;
  keyElements?: string[];
  extra?: string;
}

export interface PropAttributes {
  category?: string;
  material?: string;
  style?: string;
  size?: string;
  visualStyle?: string;
  variantName?: string;
  variantDesc?: string;
  appearance?: string;
  extra?: string;
}

export const PROP_CATEGORY_OPTIONS = [
  '武器',
  '法器',
  '饰品/首饰',
  '日常用品',
  '工具',
  '载具',
  '典籍/卷轴',
  '食物/饮品',
  '信物/令牌',
  '其他',
];

export const PROP_MATERIAL_OPTIONS = [
  '金属',
  '木质',
  '玉石/宝石',
  '布料/皮革',
  '水晶/玻璃',
  '陶瓷',
  '骨质/角',
  '混合材质',
];

export const PROP_STYLE_OPTIONS = [
  '古风',
  '仙侠',
  '科幻未来',
  '现代都市',
  '奇幻魔法',
  '蒸汽朋克',
  '暗黑哥特',
  '末日废土',
];

export const PROP_SIZE_OPTIONS = ['小型(手持)', '中型(可携带)', '大型(场景级)'];

export const CHARACTER_PERSONALITY_OPTIONS = [
  '坚韧',
  '冷静',
  '腹黑',
  '热血',
  '高冷',
  '温柔',
  '叛逆',
  '聪慧',
  '阴郁',
  '幽默',
  '深情',
  '果断',
];

export { useVideoStyles } from '@/hooks/useVideoStyles';
export type { VideoStyleMeta } from '@/hooks/useVideoStyles';
/** 角色定位 · 每项明确性别(供 Agent 拼提示词时不再二义) */
export const CHARACTER_ROLES = [
  '女主',
  '男主',
  '女二',
  '男二',
  '女反派',
  '男反派',
  '女配角',
  '男配角',
];

/** 年龄段快捷选项(用户也可在外貌补充里写更精确区间) */
export const AGE_RANGE_OPTIONS = [
  '婴儿(0-2)',
  '儿童(3-9)',
  '少年(10-17)',
  '青年(18-25)',
  '青年(26-32)',
  '中年(33-45)',
  '中年(46-55)',
  '老年(56+)',
];

export const LOCATION_KIND_OPTIONS = ['室内', '室外', '都市夜景', '古风庭院', '科幻空间', '校园', '职场办公'];

// ---------- Step 3 出图设置 ----------

/** Seedream 支持的尺寸枚举(质量等级) */
export const SEEDREAM_QUALITY_OPTIONS: { value: string; label: string; desc: string }[] = [
  { value: '2K', label: '标准 2K', desc: '速度较快 · 适合预览' },
  { value: '4K', label: '极致 4K', desc: '细节最佳 · 耗时最长' },
];

/** 出图风格(prepend 到 prompt,贯穿所有视角) */
export const IMAGE_STYLE_OPTIONS = [
  '电影感写实',
  '日漫精绘',
  '国风工笔',
  '油画质感',
  '复古胶片',
  '赛博朋克',
  '黑白线稿',
  '水墨写意',
];
